import { useState, type ReactNode } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router";
import { useStateValue } from "../../context/StateValueContext";
import { PERMISSIONS } from "../../constants/permissions";
import { hasPermission } from "../../functions/permissionUtils";
import { CmLink } from "../common/CmLink";
import type { EvidenceActivityEvent, EvidenceActivityPackageRow, EvidenceActivityTrail } from "../../types/evidenceActivity";
import { SLOW_GAP_SECONDS } from "../../types/evidenceActivity";
import { RESULT_LABELS, STEP_LABELS, detailText, formatDuration } from "./formatDuration";
import { foldSession, groupDetail } from "./trailRows";

const KEY_STEPS = new Set(["opened", "generate", "pdf_downloaded", "package_downloaded", "submitted", "abandoned"]);

const clock = (iso: string) => dayjs(iso).format("HH:mm:ss");
const short = (iso: string) => dayjs(iso).format("HH:mm");

function verdict(pkg: EvidenceActivityPackageRow): { value: string; label: string } {
  switch (pkg.outcome) {
    case "finished": return {
      value: pkg.agent ? `Submitted by ${pkg.agent}` : "Submitted",
      label: `${pkg.finishedAt ? dayjs(pkg.finishedAt).format("D MMM, HH:mm") : ""}${pkg.result ? ` · ${RESULT_LABELS[pkg.result]}` : ""}`,
    };
    case "downloaded": return { value: pkg.agent ? `Downloaded by ${pkg.agent}` : "Downloaded", label: "not submitted" };
    case "open": return { value: "Still open", label: pkg.agent ? `started by ${pkg.agent}` : "—" };
    case "abandoned": return { value: pkg.agent ? `Abandoned by ${pkg.agent}` : "Abandoned", label: "—" };
  }
}

export default function ActivityTrail({ trail, pkg, evidenceHref }: { trail: EvidenceActivityTrail; pkg: EvidenceActivityPackageRow; evidenceHref: string }) {
  const [{ user }] = useStateValue();
  // The disputes list is its own permission, and it must not inherit a company scope from this global page.
  const canOpenDispute = pkg.disputeId != null && hasPermission(user, PERMISSIONS.DISPUTE_READ);
  // One person's trail needs no names; two or more get one per sitting, or on every step of a sitting they share.
  const names = trail.users.length > 1 ? new Map(trail.users.map((u) => [u.user_id, u.name])) : null;
  const nameOf = (userId: number) => names?.get(userId) ?? `User ${userId}`;
  const maxGap = Math.max(0, ...trail.sessions.flatMap((s) => s.events.filter((e) => e.counted).map((e) => e.gapSeconds ?? 0)));
  const { value, label } = verdict(pkg);
  const buttonSx = { textTransform: "none", borderRadius: 2, bgcolor: "background.paper" };
  return (
    <Stack spacing={2} sx={{ py: 2, pl: { xs: 0, md: 5 }, pr: 1 }}>
      <Stack direction="row" spacing={3.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Figure value={formatDuration(trail.summary.timeSeconds)} label="Time on package" />
        <Figure value={String(trail.summary.steps)} label="Steps" />
        <Figure value={`${trail.summary.generates}×`} label="Generated" />
        <Figure value={String(trail.sessions.length)} label={trail.sessions.length === 1 ? "Sitting" : "Sittings"} />
        <Figure value={value} label={label} />
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          <Button component={CmLink} to={evidenceHref} size="small" variant="outlined" sx={buttonSx}>Open evidence page</Button>
          {canOpenDispute && <Button component={RouterLink} to={`/disputes?search=${pkg.disputeId}&range=all&cmStatuses=all&includeUnrepresented=true`} size="small" variant="outlined" sx={buttonSx}>Open dispute</Button>}
        </Stack>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "64px 1fr", columnGap: 1.75 }}>
        {trail.sessions.map((session, i) => {
          const sole = names && session.events.every((e) => e.userId === session.events[0].userId) ? session.events[0].userId : null;
          const stepName = names && sole == null ? nameOf : null;
          return (
            <Box key={session.startedAt} sx={{ display: "contents" }}>
              {session.idleBeforeSeconds != null && (
                <Typography variant="caption" color="text.disabled" sx={{ gridColumn: "1 / -1", fontStyle: "italic", py: 1, pl: "78px" }}>
                  {formatDuration(session.idleBeforeSeconds)} away · not counted
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ gridColumn: "1 / -1", fontWeight: 500, mb: 0.5 }}>
                {dayjs(session.startedAt).format("D MMM")} · {short(session.startedAt)} – {short(session.endedAt)} · {formatDuration(session.timeSeconds)}{sole != null && ` · ${nameOf(sole)}`}
              </Typography>
              {foldSession(session.events).map((row, j) => (row.kind === "event"
                ? <Event key={`${i}-${j}`} event={row.event} name={stepName && stepName(row.event.userId)} maxGap={maxGap} />
                : <Group key={`${i}-${j}`} events={row.events} gapSeconds={row.gapSeconds} counted={row.counted} slow={row.slow} stepName={stepName} maxGap={maxGap} />))}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

function Event({ event, name, maxGap }: { event: EvidenceActivityEvent; name: string | null; maxGap: number }) {
  return (
    <Row at={event.at} keyStep={KEY_STEPS.has(event.step)} title={STEP_LABELS[event.step] ?? event.step} name={name}
      detail={detailText(event.step, event.detail)} gapSeconds={event.counted ? event.gapSeconds : null}
      slow={event.counted && (event.gapSeconds ?? 0) >= SLOW_GAP_SECONDS} maxGap={maxGap} />
  );
}

function Group({ events, gapSeconds, counted, slow, stepName, maxGap }: {
  events: EvidenceActivityEvent[]; gapSeconds: number; counted: boolean; slow: boolean; stepName: ((userId: number) => string) | null; maxGap: number;
}) {
  const [shown, setShown] = useState(false);
  if (shown) return <>{events.map((e, i) => <Event key={i} event={e} name={stepName && stepName(e.userId)} maxGap={maxGap} />)}</>;
  return (
    <Row at={events[0].at} keyStep={false} title={`Edits ×${events.length}`} name={null} detail={groupDetail(events)} gapSeconds={counted ? gapSeconds : null} slow={slow} maxGap={maxGap}
      action={<Button size="small" onClick={() => setShown(true)} sx={{ textTransform: "none", minWidth: 0, p: 0, fontSize: 12 }}>Show {events.length}</Button>} />
  );
}

function Row({ at, keyStep, title, name, detail, gapSeconds, slow, maxGap, action }: {
  at: string; keyStep: boolean; title: string; name: string | null; detail: string; gapSeconds: number | null; slow: boolean; maxGap: number; action?: ReactNode;
}) {
  return (
    <>
      <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right", py: 0.75, fontVariantNumeric: "tabular-nums" }}>{clock(at)}</Typography>
      <Box sx={{
        position: "relative", pl: 2.25, py: 0.75, borderLeft: "2px solid", borderColor: "divider",
        display: "flex", justifyContent: "space-between", gap: 1.5,
        "&::before": {
          content: '""', position: "absolute", left: -5, top: 13, width: 8, height: 8, borderRadius: "50%",
          bgcolor: keyStep ? "primary.main" : "background.paper", border: "2px solid", borderColor: keyStep ? "primary.main" : "text.disabled",
        },
      }}>
        <Box>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
            {name && <Typography variant="caption" color="text.secondary">{`· ${name}`}</Typography>}
            {action}
          </Stack>
          {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ alignSelf: "flex-start", pt: 0.25 }}>
          {gapSeconds != null && (
            <Box aria-hidden sx={{ width: 60, height: 4, borderRadius: 2, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box sx={{ width: `${maxGap ? Math.min(1, gapSeconds / maxGap) * 100 : 0}%`, height: "100%", bgcolor: slow ? "warning.main" : "primary.light" }} />
            </Box>
          )}
          <Typography variant="caption" color={slow ? "warning.main" : "text.disabled"} sx={{ whiteSpace: "nowrap", fontWeight: slow ? 500 : 400, fontVariantNumeric: "tabular-nums" }}>
            {gapSeconds != null ? `+${formatDuration(gapSeconds)}` : ""}
          </Typography>
        </Stack>
      </Box>
    </>
  );
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
